import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '../../../../constants/routes';
import { useStartChat } from '../../../../hooks/useSupport';
import { DashboardModal } from './DashboardModal';

/** Matches StartChatDto on the backend. */
const MAX_MESSAGE_LENGTH = 4000;

interface StartChatModalProps {
  userId: string;
  onClose: () => void;
}

/**
 * The agent's first line to a user, delivered by the bot. On success the
 * conversation continues on the ticket page, where replies arrive live.
 */
export const StartChatModal = memo(function StartChatModal({
  userId,
  onClose,
}: StartChatModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const mutation = useStartChat();
  const trimmed = text.trim();

  const handleSubmit = useCallback(() => {
    if (!trimmed) return;
    mutation.mutate(
      { userId, text: trimmed },
      {
        onSuccess: (ticket) =>
          navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_SUPPORT}/${ticket._id}`),
      },
    );
  }, [trimmed, userId, mutation, navigate]);

  return (
    <DashboardModal
      titleId="start-chat-modal-title"
      icon="send"
      title={t('dashboard.userDetails.messageTelegram')}
      subtitle={t('dashboard.userDetails.messageTelegramSubtitle')}
      onClose={onClose}
      // Every failure here reads the same to an agent: the line did not reach
      // the user. The common cause is a user who blocked the bot (503).
      error={mutation.isError ? t('dashboard.userDetails.messageFailed') : null}
      cancelLabel={t('dashboard.userDetails.cancel')}
      submitLabel={t('dashboard.userDetails.send')}
      submittingLabel={t('dashboard.userDetails.sending')}
      onSubmit={handleSubmit}
      isSubmitting={mutation.isPending}
      submitDisabled={!trimmed}
    >
      <div>
        <label htmlFor="start-chat-text" className="balance-field-label">
          {t('dashboard.userDetails.messageLabel')}
        </label>
        <textarea
          id="start-chat-text"
          className="balance-reason-input"
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('dashboard.userDetails.messagePlaceholder')}
          maxLength={MAX_MESSAGE_LENGTH}
          autoFocus
        />
      </div>
    </DashboardModal>
  );
});
